import { CircularProgress, Icon, Text } from "@assetwatch/ui";
import { useEffect, useState } from "react";

const ASSESSMENT_STEPS = [
  "Matching description to asset database",
  "Scanning open work orders for duplicates",
  "Scoring priority from findings",
] as const;

const DEFAULT_MIN_DURATION_MS = 2700;

type ReliAssessmentProgressProps = {
  /** True once the real assess call has settled (success or error). */
  isComplete: boolean;
  /** Total simulated pacing across all steps. Override to 0 in tests. */
  minDurationMs?: number;
};

export function ReliAssessmentProgress({
  isComplete,
  minDurationMs = DEFAULT_MIN_DURATION_MS,
}: ReliAssessmentProgressProps) {
  const [activeIndex, setActiveIndex] = useState(0);
  const lastStepIndex = ASSESSMENT_STEPS.length - 1;

  // Advances the checklist on a fixed timer for visual pacing. The last step
  // never renders as "done" from the timer alone — only `isComplete` (the
  // real network call settling) can close it out, so the screen never
  // implies more progress than has actually happened.
  useEffect(() => {
    if (minDurationMs <= 0) {
      setActiveIndex(lastStepIndex);
      return;
    }

    const stepDurationMs = minDurationMs / ASSESSMENT_STEPS.length;
    const timeouts = ASSESSMENT_STEPS.slice(1).map((_, index) =>
      window.setTimeout(
        () => setActiveIndex(index + 1),
        stepDurationMs * (index + 1),
      ),
    );

    return () => {
      for (const timeout of timeouts) {
        window.clearTimeout(timeout);
      }
    };
  }, [minDurationMs, lastStepIndex]);

  const completedSteps =
    activeIndex + (isComplete && activeIndex === lastStepIndex ? 1 : 0);
  const progressValue = Math.round(
    (completedSteps / ASSESSMENT_STEPS.length) * 100,
  );

  return (
    <div className="flex min-h-full flex-1 flex-col items-center justify-center gap-(--spacing-24) bg-surface-container-low p-(--spacing-20)">
      <CircularProgress
        aria-label="Reviewing request"
        size="lg"
        tone="brand"
        value={progressValue}
      />
      <span className="text-center font-(family-name:--font-family-title) text-(length:--font-size-title-lg) font-(number:--font-weight-medium) leading-(--font-line-height-28) text-text-default">
        Reviewing request…
      </span>
      <div className="flex w-full max-w-[420px] flex-col gap-(--spacing-8)">
        {ASSESSMENT_STEPS.map((step, index) => {
          const isDone =
            index < activeIndex ||
            (index === activeIndex && isComplete && index === lastStepIndex);
          const isActive = index === activeIndex && !isDone;

          const stepState = isDone ? "done" : isActive ? "active" : "pending";

          return (
            <div
              className="flex items-center gap-(--spacing-10) rounded-(--radius-lg) bg-surface p-(--spacing-12) shadow-elevation-1"
              data-step-state={stepState}
              key={step}
            >
              <span className="flex size-(--spacing-24) shrink-0 items-center justify-center">
                {isDone ? (
                  <Icon name="check-circle" size="sm" tone="positive" />
                ) : isActive ? (
                  <span className="aw-spinner inline-flex" aria-hidden="true">
                    <Icon
                      name="button-load-spinner"
                      size="sm"
                      tone="secondary"
                    />
                  </span>
                ) : (
                  <span className="size-(--spacing-8) rounded-full bg-outline-subtle" />
                )}
              </span>
              <Text size="sm" tone={isActive || isDone ? "default" : "subtle"}>
                {step}
              </Text>
            </div>
          );
        })}
      </div>
    </div>
  );
}
